import { Link } from '@tanstack/react-router';
import { CalendarClock, MapPin } from 'lucide-react';

import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import {
    getNumberValue,
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import {
    DetailChipList,
    formatDetailStatus,
    getStringArray,
    joinPresent,
    TOOL_SURFACE_HOVER_CLASS,
    ToolToken,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { cn } from '@apps/web/lib/utils';

type ShiftInfo = {
    readonly shiftId: string | null;
    readonly patientName: string;
    readonly date: string | null;
    readonly start: string | null;
    readonly end: string | null;
    readonly durationHours: number | null;
    readonly location: string | null;
    readonly cityState: string | null;
    readonly status: string | null;
    readonly role: string | null;
    readonly skills: Array<string>;
};

function getNestedRecord(
    record: Record<string, unknown>,
    key: string,
): Record<string, unknown> | null {
    const value = record[key];
    return isRecord(value) ? value : null;
}

function getFirstString(
    records: ReadonlyArray<Record<string, unknown> | null>,
    keys: ReadonlyArray<string>,
): string | null {
    for (const record of records) {
        for (const key of keys) {
            const value = getStringValue(record, key);
            if (value) {
                return value;
            }
        }
    }
    return null;
}

function getFirstNumber(
    records: ReadonlyArray<Record<string, unknown> | null>,
    keys: ReadonlyArray<string>,
): number | null {
    for (const record of records) {
        for (const key of keys) {
            const value = getNumberValue(record, key);
            if (value !== null) {
                return value;
            }
        }
    }
    return null;
}

function getFirstStringArray(
    records: ReadonlyArray<Record<string, unknown> | null>,
    keys: ReadonlyArray<string>,
): Array<string> {
    for (const record of records) {
        for (const key of keys) {
            const value = getStringArray(record?.[key]);
            if (value.length > 0) {
                return value;
            }
        }
    }
    return [];
}

function isIsoLikeDateTime(value: string | null): value is string {
    return Boolean(
        value &&
        /^\d{4}-\d{2}-\d{2}/u.test(value) &&
        !Number.isNaN(Date.parse(value)),
    );
}

function formatIsoDate(value: string, timezone: string): string {
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
    }).format(new Date(value));
}

function formatIsoTime(value: string, timezone: string): string {
    return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
    }).format(new Date(value));
}

function formatDurationHours(hours: number): string {
    const rounded = Math.round(hours * 10) / 10;
    const label = Number.isInteger(rounded)
        ? String(rounded)
        : rounded.toFixed(1);
    return `${label} ${rounded === 1 ? 'hour' : 'hours'}`;
}

function getDurationHours({
    start,
    end,
    providedHours,
}: {
    readonly start: string | null;
    readonly end: string | null;
    readonly providedHours: number | null;
}): number | null {
    if (providedHours !== null && providedHours >= 0) {
        return providedHours;
    }
    if (!start || !end) {
        return null;
    }
    const startMs = Date.parse(start);
    const endMs = Date.parse(end);
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
        return null;
    }
    return (endMs - startMs) / (60 * 60 * 1000);
}

function getShiftInfo(shift: Record<string, unknown>): ShiftInfo {
    const patient = getNestedRecord(shift, 'patient');
    const timing = getNestedRecord(shift, 'timing');
    const assignment = getNestedRecord(shift, 'assignment');
    const requirements = getNestedRecord(shift, 'requirements');
    const start = getFirstString(
        [timing, shift],
        [
            'start',
            'start_time',
            'starts_at',
            'shift_start_time',
            'scheduled_start_time',
            'scheduled_start',
        ],
    );
    const end = getFirstString(
        [timing, shift],
        [
            'end',
            'end_time',
            'ends_at',
            'shift_end_time',
            'scheduled_end_time',
            'scheduled_end',
        ],
    );

    return {
        shiftId: getFirstString([shift], ['shift_id', 'id']),
        patientName:
            getFirstString([patient, shift], ['name', 'patient_name']) ??
            getStringValue(shift, 'client_name') ??
            'Shift',
        date: getFirstString(
            [timing, shift],
            ['date', 'shift_day', 'shift_date'],
        ),
        start,
        end,
        durationHours: getDurationHours({
            start,
            end,
            providedHours: getFirstNumber(
                [timing, shift],
                ['duration_hours', 'scheduled_hours', 'hours'],
            ),
        }),
        location: joinPresent([
            getFirstString([patient, shift], ['address', 'address_line_1']),
            getFirstString([patient, shift], ['address_line_2']),
            getFirstString([patient, shift], ['city']),
            getFirstString([patient, shift], ['state']),
        ]),
        cityState: joinPresent([
            getFirstString([patient, shift], ['city']),
            getFirstString([patient, shift], ['state']),
        ]),
        status: getFirstString([assignment, shift], ['status']),
        role: getFirstString([requirements, shift], ['role', 'role_required']),
        skills: getFirstStringArray(
            [requirements, shift],
            ['skills', 'skills_required'],
        ),
    };
}

function getStatusTone(
    status: string | null,
): 'blue' | 'green' | 'neutral' | 'red' {
    const normalized = status?.toLowerCase() ?? '';
    if (
        normalized.includes('need') ||
        normalized.includes('open') ||
        normalized.includes('coverage') ||
        normalized.includes('unassigned')
    ) {
        return 'red';
    }
    if (
        normalized.includes('assigned') ||
        normalized.includes('scheduled') ||
        normalized.includes('filled') ||
        normalized.includes('complete')
    ) {
        return 'green';
    }
    if (normalized.includes('progress') || normalized.includes('pending')) {
        return 'blue';
    }
    return 'neutral';
}

function getTimingLabels(shift: ShiftInfo, timezone: string): Array<string> {
    const dateLabel = shift.date
        ? isIsoLikeDateTime(shift.date)
            ? formatIsoDate(shift.date, timezone)
            : shift.date
        : isIsoLikeDateTime(shift.start)
          ? formatIsoDate(shift.start, timezone)
          : null;
    const startLabel = isIsoLikeDateTime(shift.start)
        ? formatIsoTime(shift.start, timezone)
        : shift.start;
    const endLabel = isIsoLikeDateTime(shift.end)
        ? formatIsoTime(shift.end, timezone)
        : shift.end;
    const timeLabel =
        startLabel || endLabel
            ? `${startLabel ?? ''}${startLabel && endLabel ? ' - ' : ''}${endLabel ?? ''}`
            : null;
    const durationLabel =
        shift.durationHours !== null
            ? formatDurationHours(shift.durationHours)
            : null;
    return [dateLabel, timeLabel, durationLabel, shift.cityState].filter(
        (label): label is string => Boolean(label),
    );
}

function ShiftTitle({
    shift,
    linkToShift,
}: {
    readonly shift: ShiftInfo;
    readonly linkToShift: boolean;
}) {
    if (linkToShift && shift.shiftId) {
        return (
            <Link
                to="/shifts/$shift-id"
                params={{ 'shift-id': shift.shiftId }}
                className="text-primary decoration-primary/40 hover:decoration-primary rounded-sm text-sm font-semibold underline underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                onClick={(event) => event.stopPropagation()}
            >
                {shift.patientName}
            </Link>
        );
    }
    return (
        <p className="text-foreground text-sm font-semibold">
            {shift.patientName}
        </p>
    );
}

export function ShiftInfoCard({
    shift,
    linkToShift = false,
}: {
    readonly shift: Record<string, unknown>;
    readonly linkToShift?: boolean;
}) {
    const { timezone } = usePhoebeChatOrganizationScope();
    const info = getShiftInfo(shift);
    const timingLabels = getTimingLabels(info, timezone);
    const requirementValues = [info.role, ...info.skills].filter(
        (value): value is string => Boolean(value),
    );
    const statusLabel = formatDetailStatus(info.status);

    return (
        <div
            className={cn(
                'border-border/60 bg-card rounded-lg border px-2 py-1.5',
                TOOL_SURFACE_HOVER_CLASS,
            )}
        >
            <div className="flex items-start gap-2.5">
                <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md border border-blue-100 bg-blue-50 text-blue-700">
                    <CalendarClock className="size-3" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                            <ShiftTitle
                                shift={info}
                                linkToShift={linkToShift}
                            />
                            {timingLabels.length > 0 ? (
                                <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                                    {timingLabels.map((label) => (
                                        <span key={label}>{label}</span>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                        {statusLabel ? (
                            <ToolToken tone={getStatusTone(info.status)}>
                                {statusLabel}
                            </ToolToken>
                        ) : null}
                    </div>
                    {info.location && info.location !== info.cityState ? (
                        <div className="text-muted-foreground mt-1 flex items-center gap-1 text-[11px]">
                            <MapPin className="size-3 flex-shrink-0" />
                            <span className="min-w-0 truncate">
                                {info.location}
                            </span>
                        </div>
                    ) : null}
                    {requirementValues.length > 0 ? (
                        <div className="mt-2">
                            <DetailChipList values={requirementValues} />
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
